/* eslint-disable */
import PropTypes from 'prop-types';
import { useEffect, useState, forwardRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import { useSelector } from 'react-redux';
import { Sidebar, Menu, MenuItem, SubMenu, useProSidebar } from 'react-pro-sidebar';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import useResponsive from '../../hooks/useResponsive';
import navConfig from './NavConfig';
import Logo from '../../assets/logo/logo.png';
import { getCachedBranding } from '../../services/brandingService';
import './style.css';

const routeAliases = {
  '/app/sources': ['/app/source-chapters', '/app/chapter-questions'],
  '/app/questions': ['/app/question-details', '/app/question-edit', '/app/question-create'],
  '/app/assessments': ['/app/assessmentCreate', '/app/general-test'],
  '/app/course': ['/app/course-type', '/app/course-content', '/app/submodules', '/app/simulation-preview'],
  '/app/content-writer-details': [
    '/app/content-writer-detailsnew',
    '/app/content-writer-detailsview',
    '/app/content-writer-edit',
  ],
  '/app/individual-users': ['/app/individual-usersview'],
  '/app/client-users': ['/app/client-details'],
  '/app/learners': ['/app/learner-profile'],
  '/app/ebook-list': ['/app/ebook-creator'],
};

const normalize = (p) => p?.replace(/\/+$/, '');

const TooltipAnchor = forwardRef(({ children, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    style={{ display: 'block', isolation: 'isolate' }}
  >
    {children}
  </div>
));
TooltipAnchor.displayName = 'TooltipAnchor';

const activeStyles = {
  '.ps-active': {
    background: '#A3ADB8 !important',
    color: '#3A3FE0 !important',
  },
};

const SideNavBar = ({ item, collapsed }) => {
  const { pathname, state } = useLocation();
  const fromChapterPage = state?.fromChapterPage;
  const { title, path, icon, children } = item;

  const isActive =
    normalize(pathname) === normalize(path) ||
    normalize(pathname).startsWith(`${normalize(path)}/`) ||
    (routeAliases[path] &&
      !fromChapterPage &&
      routeAliases[path].some((alias) =>
        normalize(pathname).startsWith(alias)
      ));

  if (children) {
    return (
      <SubMenu
        label={
          collapsed ? (
            <Tooltip title={title} placement="right" arrow>
              <span>{title}</span>
            </Tooltip>
          ) : (
            title
          )
        }
        icon={
          icon && (
            <span style={{ fontSize: '18px', display: 'flex' }}>
              {icon}
            </span>
          )
        }
        rootStyles={{
          marginBottom: '8px',
          paddingLeft: '12px',
          paddingRight: '12px',
        }}
      >
        {children.map((child) => {
          const childActive = pathname === child.path;

          const childItem = (
            <MenuItem
              key={child.path}
              component={<RouterLink to={child.path} />}
              active={childActive}
              rootStyles={{
                marginBottom: '6px',
                paddingLeft: '12px',
                paddingRight: '12px',
                '.ps-menu-button': {
                  height: '40px',
                  borderRadius: '8px',
                  paddingLeft: collapsed ? '0px' : '20px',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                },
                '.ps-active': {
                  background: '#A3ADB8 !important',
                  color: '#3A3FE0',
                },
              }}
            >
              <span
                title={child.title}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '160px',
                  fontSize: '0.75rem',
                }}
              >
                {child.title}
              </span>
            </MenuItem>
          );

          return collapsed ? (
            <Tooltip key={child.path} title={child.title} placement="right" arrow>
              <div>{childItem}</div>
            </Tooltip>
          ) : (
            childItem
          );
        })}
      </SubMenu>
    );
  }

  const menuButton = (
    <MenuItem
      component={<RouterLink to={path} />}
      active={isActive}
      icon={
        icon && (
          <span style={{ fontSize: '18px', display: 'flex' }}>
            {icon}
          </span>
        )
      }
      rootStyles={{
        marginBottom: '6px',
        paddingLeft: '12px',
        paddingRight: '12px',
        '.ps-menu-button': {
          height: '44px',
          borderRadius: '10px',
          paddingLeft: collapsed ? '0px' : '16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
        },
        '.ps-active': {
          background: '#A3ADB8 !important',
          color: '#3A3FE0',
        },
      }}
    >
      <span
        title={title}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '160px',
        }}
      >
        {title}
      </span>
    </MenuItem>
  );

  return collapsed ? (
    <Tooltip title={title} placement="right" arrow>
      <div>{menuButton}</div>
    </Tooltip>
  ) : (
    menuButton
  );
};

const SidebarContent = ({ layoutType, collapsed, handleCollapseSidebar, logoSrc }) => (
  <div
    style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      isolation: 'isolate',
      contain: 'layout paint',
    }}
  >
    {/* ── Logo area ── */}
    <div
      style={{
        padding: '12px 16px 10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: '60px',
        flexShrink: 0,
        overflow: 'hidden',   // ← prevents large logo from bleeding out
      }}
    >
      <img
        src={logoSrc}
        alt="logo"
        style={{
          width: collapsed ? '36px' : '112px',
          maxWidth: '100%',                          // ← never wider than container
          maxHeight: collapsed ? '36px' : '48px',   // ← clamps tall logos
          transition: 'width 0.3s ease',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>

    <Divider sx={{ borderColor: '#e2e8f0', mx: 1, mb: 1, flexShrink: 0 }} />

    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '0 4px',
        clipPath: 'inset(0)',
      }}
    >
      <Menu
        menuItemStyles={{
          icon: {
            minWidth: '28px',
            fontSize: '18px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          root: {
            backgroundColor: 'transparent',
          },
        }}
      >
        {layoutType?.map((section) => {
          // Items with icon === null are metadata-only entries used by the Roles
          // permission picker — they should not appear as sidebar links.
          const visibleItems = (section?.items || []).filter((item) => item.icon !== null);
          if (visibleItems.length === 0) return null;
          const visibleSection = { ...section, items: visibleItems };
          return (
          <div key={visibleSection?.subheader || 'top'} style={{ marginBottom: '10px' }}>
            {!collapsed && visibleSection?.subheader && (
              <p
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: '#9ca3af',
                  padding: '14px 16px 4px',
                  margin: 0,
                  letterSpacing: '0.08em',
                  userSelect: 'none',
                }}
              >
                {visibleSection.subheader}
              </p>
            )}
            {visibleItems.map((item) => (
              <SideNavBar key={item.title} item={item} collapsed={collapsed} />
            ))}
          </div>
          );
        })}
      </Menu>
    </div>

    <div
      role="button"
      tabIndex={0}
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      onClick={handleCollapseSidebar}
      onKeyDown={(e) => e.key === 'Enter' && handleCollapseSidebar()}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#141583';
        e.currentTarget.style.color = '#ffffff';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = '#4b5563';
      }}
      style={{
        flexShrink: 0,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        cursor: 'pointer',
        borderTop: '1px solid #e2e8f0',
        color: '#4b5563',
        transition: 'background-color 0.2s, color 0.2s',
        outline: 'none',
        userSelect: 'none',
        isolation: 'isolate',
      }}
    >
      {!collapsed ? (
        <>
          <KeyboardDoubleArrowLeftIcon
            style={{ marginRight: '8px', fontSize: '20px', color: 'inherit' }}
          />
          <span style={{ fontSize: '0.875rem', color: 'inherit' }}>Collapse sidebar</span>
        </>
      ) : (
        <KeyboardDoubleArrowRightIcon style={{ fontSize: '20px', color: 'inherit' }} />
      )}
    </div>
  </div>
);

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { collapseSidebar, collapsed } = useProSidebar();
  const isDesktop = useResponsive('up', 'lg');
  const [layoutType, setLayoutType] = useState([]);
  const userInfo = useSelector((state) => state.userInfo);

  const branding = getCachedBranding();
  const logoSrc = branding?.logoUrl || Logo;

  useEffect(() => {
    // role_lk.layout picks the menu; the legacy role checks remain as the fallback
    // for sessions issued before layout shipped and for roles without one set.
    const layout = userInfo?.layout
      || { SUPER_ADMIN: 'ADMIN', MANAGER: 'CLIENT', CLIENT_ADMIN: 'CLIENT', CONTENT_WRITER: 'CONTENT' }[userInfo?.role]
      || 'CONTENT';

    let menu;
    if (layout === 'ADMIN') {
      menu = navConfig.adminNavConfig;
    } else if (layout === 'CLIENT') {
      if (userInfo?.role === 'MANAGER') {
        menu = navConfig.managerNavConfig;
      } else {
        const isFullCreator = userInfo?.featureToggleMode === 'FULL_CREATOR';
        menu = navConfig.clientNavConfig.map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => item.path !== '/app/content-bank' || isFullCreator
          ),
        }));
      }
    } else {
      menu = navConfig.contentNavConfig;
    }

    // role_lk.allowed_pages narrows the menu to the pages this role may open.
    // null/empty means no restriction, so existing roles see their full menu.
    const allowed = Array.isArray(userInfo?.allowedPages) && userInfo.allowedPages.length
      ? new Set(userInfo.allowedPages)
      : null;
    if (allowed) {
      menu = menu.map((section) => ({
        ...section,
        items: section.items.filter((item) => allowed.has(item.path)),
      }));
    }

    setLayoutType(menu.filter((section) => section.items.length > 0));
  }, [userInfo]);

  useEffect(() => {
    if (isOpenSidebar && onCloseSidebar) onCloseSidebar();
  }, [pathname]);

  const handleCollapseSidebar = () => collapseSidebar();

  return (
    <>
      {isDesktop && (
        <Sidebar
          backgroundColor="#ffffff"
          style={{
            position: 'fixed',
            height: '100vh',
            zIndex: 1100,
            borderRight: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
          width="270px"
          collapsedWidth="74px"
        >
          <SidebarContent
            layoutType={layoutType}
            collapsed={collapsed}
            handleCollapseSidebar={handleCollapseSidebar}
            logoSrc={logoSrc}
          />
        </Sidebar>
      )}

      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          variant="temporary"
          anchor="left"
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: 270,
              backgroundColor: '#ffffff',
              borderRight: '1px solid #e2e8f0',
              overflow: 'hidden',
            },
          }}
        >
          <SidebarContent
            layoutType={layoutType}
            collapsed={false}
            handleCollapseSidebar={onCloseSidebar}
            logoSrc={logoSrc}  
          />
        </Drawer>
      )}
    </>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};